import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { Router, RouterLink, RouterOutlet } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import {
  KsgAppLayout,
  KsgTopbar,
  KsgThemeToggle,
  KsgSidebarFooterDef,
  KsgSessionTimer,
  KsgLanguageSelector,
  KsgUserPanel,
  KsgButton,
  KsgToast,
  ksgRouterActiveUrl,
  type KsgSidebarSection,
} from '@ksg/ui';
import { SessionService } from '../core/session/session.service';
import { AuthService } from '../core/auth/auth.service';
import { BrandLogo } from './brand-logo';
import { CompanySwitcher } from './component/company-switcher/company-switcher';
import { TimeTracker } from './component/time-tracker/time-tracker';
import { TourButton } from '../shared/tour/tour-button';
import { TourOverlay } from '../shared/tour/tour-overlay';
import { DemoNotice } from '../shared/demo/demo-notice';

interface NavEntry {
  key: string;
  icon: string;
  url: string;
}

const NAV: readonly NavEntry[] = [
  { key: 'dashboard', icon: 'pi-gauge', url: '/' },
  { key: 'bookkeeping', icon: 'pi-book', url: '/bookkeeping' },
  { key: 'chartOfAccounts', icon: 'pi-sitemap', url: '/ledger' },
  { key: 'journal', icon: 'pi-book', url: '/ledger/dziennik' },
  { key: 'generalLedger', icon: 'pi-calculator', url: '/ledger/obroty-salda' },
  { key: 'kpir', icon: 'pi-table', url: '/ledger/kpir' },
  { key: 'incomeStatement', icon: 'pi-chart-line', url: '/ledger/rzis' },
  { key: 'openingBalances', icon: 'pi-flag-fill', url: '/ledger/bilans-otwarcia' },
  { key: 'balanceSheet', icon: 'pi-images', url: '/ledger/bilans' },
  { key: 'periods', icon: 'pi-calendar-clock', url: '/ledger/okresy' },
  { key: 'autoPosting', icon: 'pi-bolt', url: '/ledger/auto-ksiegowania' },
  { key: 'vatRegisters', icon: 'pi-list', url: '/bookkeeping/vat-registers' },
  { key: 'declarations', icon: 'pi-file-edit', url: '/declarations' },
  { key: 'submissions', icon: 'pi-send', url: '/declarations/submissions' },
  { key: 'taxSettlements', icon: 'pi-lock', url: '/tax-settlements' },
  { key: 'vehicles', icon: 'pi-car', url: '/vehicles' },
  { key: 'fixedAssets', icon: 'pi-building', url: '/fixed-assets' },
  { key: 'timeReports', icon: 'pi-clock', url: '/time-reports' },
  { key: 'messages', icon: 'pi-comments', url: '/messages' },
  { key: 'fiscalReports', icon: 'pi-print', url: '/fiscal-reports' },
  { key: 'comarchExport', icon: 'pi-desktop', url: '/comarch' },
  { key: 'tutorial', icon: 'pi-compass', url: '/samouczek' },
  { key: 'support', icon: 'pi-flag', url: '/zgloszenia' },
] as const;

@Component({
  selector: 'app-shell',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterOutlet,
    RouterLink,
    TranslocoPipe,
    KsgAppLayout,
    KsgTopbar,
    KsgThemeToggle,
    KsgSidebarFooterDef,
    KsgSessionTimer,
    KsgLanguageSelector,
    KsgUserPanel,
    KsgButton,
    KsgToast,
    BrandLogo,
    CompanySwitcher,
    TimeTracker,
    TourButton,
    TourOverlay,
    DemoNotice,
  ],
  template: `
    <ksg-app-layout
      [sections]="sections()"
      [activeUrl]="activeUrl()"
      storageKey="accountancy-sidebar-collapsed"
      [sidebarAriaLabel]="'topbar.mainNav' | transloco"
      [sidebarCollapseLabel]="'topbar.collapseMenu' | transloco"
      [sidebarExpandLabel]="'topbar.expandMenu' | transloco"
    >
      <ksg-topbar appTopbar showMenuButton [menuButtonLabel]="'topbar.toggleMenu' | transloco">
        <a topbarStart class="app-brand-link" routerLink="/" [attr.aria-label]="'topbar.home' | transloco">
          <app-brand-logo />
        </a>

        <ng-container topbarEnd>
          <app-time-tracker />
          <ksg-session-timer
            [active]="auth.isAuthenticated()"
            [remainingSeconds]="session.remainingSeconds()"
            [isWarning]="session.isWarning()"
            [displayTime]="session.displayTime()"
            (extend)="session.extend()"
            (logout)="auth.logout()"
          />
          <ksg-theme-toggle
            showContrast
            [toLightLabel]="'topbar.themeLight' | transloco"
            [toDarkLabel]="'topbar.themeDark' | transloco"
            [contrastLabel]="'topbar.contrast' | transloco"
          />
          <ksg-button
            variant="text"
            iconOnly
            rounded
            icon="pi-flag"
            routerLink="/zgloszenia"
            [queryParams]="{ new: 1 }"
            [ariaLabel]="'topbar.reportProblem' | transloco"
          />
          <app-tour-button />
          <ksg-language-selector />
        </ng-container>
      </ksg-topbar>

      <ng-template appSidebarFooter let-collapsed>
        <app-company-switcher [collapsed]="collapsed" />
        <ksg-user-panel
          [name]="'topbar.admin' | transloco"
          [role]="'topbar.adminRole' | transloco"
          [collapsed]="collapsed"
          [menuLabel]="'topbar.userMenu' | transloco"
          [logoutLabel]="'auth.logout' | transloco"
          (logout)="auth.logout()"
        />
      </ng-template>

      <app-demo-notice variant="bar" />
      <router-outlet />
    </ksg-app-layout>

    <ksg-toast position="top-right" />
    <app-tour-overlay />
  `,
  styles: [
    `
      .app-brand-link {
        display: inline-flex;
        align-items: center;
        text-decoration: none;
        color: inherit;
        outline: none;
        border-radius: var(--r-sm);
      }
      .app-brand-link:focus-visible {
        box-shadow: 0 0 0 3px var(--focus);
      }
    `,
  ],
})
export class AppShell {
  protected readonly session = inject(SessionService);
  protected readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly transloco = inject(TranslocoService);

  private readonly rawUrl = ksgRouterActiveUrl();

  private readonly i18nReady = toSignal(this.transloco.selectTranslate('menu.dashboard'));

  protected readonly activeUrl = computed(() => this.rawUrl().split('?')[0].split('#')[0]);

  protected readonly sections = computed<KsgSidebarSection[]>(() => {
    this.i18nReady();
    const t = (k: string) => this.transloco.translate(k);
    return [
      {
        items: NAV.map((n) => ({
          label: t('menu.' + n.key),
          icon: n.icon,
          url: n.url,
          command: () => void this.router.navigate([n.url]),
        })),
      },
    ];
  });
}
